import posthog, { type CaptureResult, type PostHogConfig } from 'posthog-js';

const MAX_HOST_POSTHOG_PROPERTY_DEPTH = 8;

function normalizeHostPostHogPropertyKey(key: string): string {
    return key.normalize('NFKC').trim().replace(/[\s_-]+/g, '').toLocaleLowerCase('en-US');
}

const BLOCKED_HOST_POSTHOG_PROPERTY_KEYS = new Set<string>([
    'document_id',
    'documentId',
    'error',
    'error_message',
    'errorMessage',
    'error_reason',
    'errorReason',
    'failure_reason',
    'failureReason',
    'file',
    'file_key',
    'fileKey',
    'file_name',
    'fileName',
    'filename',
    'infoMessage',
    'info_message',
    'prompt',
    'prompt_text',
    'promptText',
    'query',
    'question',
    'raw_text',
    'rawText',
    'return_to',
    'returnTo',
    'selected_item',
    'selectedItem',
    'selectedText',
    'selected_text',
    'text',
    'workflow_title',
    'workflowTitle',
    'workspace_name',
    'workspaceName',
    '선택 항목',
    '선택된 텍스트',
    '질문',
    '질의',
    '오류',
    '파일',
    '파일명',
    '프롬프트',
    '텍스트',
    '__proto__',
    'constructor',
    'prototype',
].map(normalizeHostPostHogPropertyKey));

const HOST_POSTHOG_URL_PROPERTY_KEYS = new Set<string>([
    '$current_url',
    '$pathname',
    '$referrer',
    'current_url',
    'currentUrl',
    'from',
    'href',
    'navigation_target',
    'path',
    'redirect_url',
    'redirectUrl',
    'referrer',
    'route',
    'to',
    'url',
].map(normalizeHostPostHogPropertyKey));
const HOST_POSTHOG_NESTED_PROPERTY_KEYS = new Set<string>(['$set', '$set_once'].map(normalizeHostPostHogPropertyKey));

interface TrackEventOptions {
    tags?: string[];
    properties?: Record<string, unknown>;
}

type HostPostHogPrivacyOptions = Pick<PostHogConfig, 'autocapture' | 'before_send' | 'capture_pageview' | 'disable_session_recording'>;

export type DataLayerWindow = Window & {
    dataLayer?: Array<Record<string, unknown>>;
};

function stripQueryAndHash(value: string): string {
    const boundaryIndex = value.search(/[?#]/);
    return boundaryIndex === -1 ? value : value.slice(0, boundaryIndex);
}

function sanitizeHostPostHogUrl(value: string): string {
    const withoutQueryOrHash = stripQueryAndHash(value);
    const originMatch = withoutQueryOrHash.match(/^(?:[a-z][a-z\d+.-]*:)?\/\/[^/]+/i);
    const origin = originMatch?.[0] ?? '';
    const path = withoutQueryOrHash.slice(origin.length);
    if (!path.startsWith('/')) return withoutQueryOrHash;

    const segments = path.split('/');
    const businessAgentIndex = segments.findIndex(segment => segment.toLocaleLowerCase('en-US') === 'business-agent');
    if (businessAgentIndex > 0) segments[businessAgentIndex - 1] = ':workspace';

    return `${origin}${segments
        .map(segment => {
            if (/^\d+-\d+$/.test(segment)) return ':workspace';
            if (/^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(segment)) return ':id';
            return segment;
        })
        .join('/')}`;
}

function isHostPostHogPropertyBag(value: unknown): value is Readonly<Record<string, unknown>> {
    // Date has no own enumerable properties, so bag-sanitizing it flattens the value
    // into {}; it serializes as an ISO string and carries no keys to sanitize.
    return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);
}

const HOST_POSTHOG_URL_KEY_TOKENS = new Set<string>(['href', 'path', 'pathname', 'redirect', 'referrer', 'route', 'uri', 'url']);

function extractHostPostHogKeyTokens(key: string): readonly string[] {
    return key
        .normalize('NFKC')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .toLocaleLowerCase('en-US')
        .split(/[^a-z0-9]+/)
        .filter(token => token.length > 0);
}

// Substring matching over the separator-stripped key over-matched unrelated keys
// ("jurisdiction"/"security" both contain "uri"), so URL detection only accepts
// whole key tokens plus URL-ish suffixes of the normalized key.
function isHostPostHogUrlPropertyKey(key: string, normalizedKey: string): boolean {
    return (
        HOST_POSTHOG_URL_PROPERTY_KEYS.has(normalizedKey) ||
        extractHostPostHogKeyTokens(key).some(token => HOST_POSTHOG_URL_KEY_TOKENS.has(token)) ||
        normalizedKey.endsWith('url') ||
        normalizedKey.endsWith('uri') ||
        normalizedKey.endsWith('href') ||
        normalizedKey.endsWith('path') ||
        normalizedKey.endsWith('pathname')
    );
}

interface SanitizedHostPostHogValue {
    readonly included: boolean;
    readonly value?: unknown;
}

function sanitizeHostPostHogValue(value: unknown, depth: number, ancestors: WeakSet<object>): SanitizedHostPostHogValue {
    if (depth > MAX_HOST_POSTHOG_PROPERTY_DEPTH) return { included: false };

    if (Array.isArray(value)) {
        if (ancestors.has(value)) return { included: false };
        ancestors.add(value);
        const sanitized: unknown[] = [];
        value.forEach(item => {
            const nested = sanitizeHostPostHogValue(item, depth + 1, ancestors);
            if (nested.included) sanitized.push(nested.value);
        });
        ancestors.delete(value);
        return { included: true, value: sanitized };
    }

    if (isHostPostHogPropertyBag(value)) {
        if (ancestors.has(value)) return { included: false };
        return { included: true, value: sanitizeHostPostHogProperties(value, depth, ancestors) };
    }

    return { included: true, value };
}

function sanitizeHostPostHogProperties(source: Readonly<Record<string, unknown>>, depth = 0, ancestors = new WeakSet<object>()): Record<string, unknown> {
    const properties: Record<string, unknown> = {};
    ancestors.add(source);

    for (const [key, value] of Object.entries(source)) {
        const normalizedKey = normalizeHostPostHogPropertyKey(key);
        if (BLOCKED_HOST_POSTHOG_PROPERTY_KEYS.has(normalizedKey)) continue;

        if (HOST_POSTHOG_NESTED_PROPERTY_KEYS.has(normalizedKey) && !isHostPostHogPropertyBag(value)) {
            continue;
        }

        if (isHostPostHogUrlPropertyKey(key, normalizedKey) && typeof value === 'string') {
            properties[key] = sanitizeHostPostHogUrl(value);
            continue;
        }

        const nested = sanitizeHostPostHogValue(value, depth + 1, ancestors);
        if (nested.included) properties[key] = nested.value;
    }

    ancestors.delete(source);
    return properties;
}

export function sanitizeHostPostHogEvent(captureResult: CaptureResult | null): CaptureResult | null {
    if (captureResult === null) return null;

    return {
        ...captureResult,
        properties: sanitizeHostPostHogProperties(captureResult.properties),
        ...(captureResult.$set === undefined ? {} : { $set: sanitizeHostPostHogProperties(captureResult.$set) }),
        ...(captureResult.$set_once === undefined ? {} : { $set_once: sanitizeHostPostHogProperties(captureResult.$set_once) }),
    };
}

export const HOST_POSTHOG_PRIVACY_OPTIONS = {
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: true,
    before_send: sanitizeHostPostHogEvent,
} satisfies HostPostHogPrivacyOptions;

export function trackEvent(eventName: string, options: TrackEventOptions = {}) {
    if (import.meta.env.DEV) {
        return;
    }

    const { tags, properties } = options;

    const payload: Record<string, unknown> = { ...(properties ?? {}) };

    if (tags && tags.length > 0) {
        payload.tags = tags;
    }

    posthog.capture(eventName, payload);
}

export function pushDataLayerEvent(eventName: string, enabled = true) {
    const browserWindow = (globalThis as typeof globalThis & { window?: DataLayerWindow }).window;
    if (!enabled || browserWindow === undefined) {
        return;
    }

    browserWindow.dataLayer ??= [];
    browserWindow.dataLayer.push({ event: eventName });
}
